// React
import { useEffect, useState } from "react"

// Context
import { useLang } from "@/context/lang"

// Types
import { UsersUserProfileLanguageChoices } from "@/api/graphql/types"

// UI
import { Disclaimer } from "@/components/Disclaimer"

// I18n
import { useTrans } from "@/lib/i18n"

/**
 * Custom hook to force default language and manage language disclaimer
 * Used on pages that don't have legal texts in other languages
 * @param fallbackLanguage - The language to fallback to (defaults to Dutch)
 */
export const useForceDefaultLanguage = (
	fallbackLanguage: UsersUserProfileLanguageChoices = UsersUserProfileLanguageChoices.Nl,
) => {
	// Translate
	const t = useTrans("investor")
	const { lang, setLang } = useLang()

	// State
	const [showLanguageDisclaimer, setShowLanguageDisclaimer] =
		useState<boolean>(false)

	// Hack to always reset to specified fallback language on this page because we don't have the legal texts in other languages yet
	useEffect(() => {
		if (lang !== fallbackLanguage) {
			setShowLanguageDisclaimer(true)
			setLang(fallbackLanguage)
		}
	}, [lang, setLang, fallbackLanguage])

	/**
	 * DefaultLanguageDisclaimerComponent
	 * @returns
	 */
	const DefaultLanguageDisclaimerComponent = () => {
		return showLanguageDisclaimer === true ? (
			<Disclaimer
				className="mb-3"
				title={t(
					"investor:investor.generic.legal-disclaimer.default-language.title",
				)}
				message={t(
					"investor:investor.generic.legal-disclaimer-default-language.message",
				)}
			/>
		) : null
	}

	// We only have to export the disclaimer
	return {
		DefaultLanguageDisclaimerComponent,
	}
}
